// "Rotate for the best experience" (§14.6): a dismissible sticker on touch phones held in portrait
// while playing (portrait stays playable). Shown once per session. It never blocks play: the
// sticker lets taps through to whatever is under it (only its ✕ is a button), the first tap
// anywhere dismisses it, and it leaves on its own after a few seconds.
import type { ScreenId } from '../contracts';
import type { UiDeps } from '../deps';
import { icon } from '../icons';
import { el, setHidden } from '../ui';

const PLAY_SCREENS: ReadonlySet<ScreenId> = new Set<ScreenId>(['lobby', 'game', 'podium', 'spectator']);
/** Shown this long at most. */
const AUTO_HIDE_MS = 6000;
/** Taps in the first moments do not count (the tap that started the round, say). */
const TAP_GRACE_MS = 400;
const LEAVE_MS = 240;

export interface RotatePrompt { readonly el: HTMLElement; update(screen: ScreenId): void }

export function createRotatePrompt(deps: UiDeps): RotatePrompt {
  let dismissed = false;
  let shownAt = -1;
  function dismiss(): void {
    if (dismissed) return;
    dismissed = true;
    root.classList.add('leave');
    window.removeEventListener('pointerdown', onTap, true);
    setTimeout(() => setHidden(root, true), LEAVE_MS);
  }
  function onTap(): void {
    if (shownAt >= 0 && performance.now() - shownAt > TAP_GRACE_MS) dismiss();
  }
  const close = el('button', { class: 'rp-x', attrs: { type: 'button', 'aria-label': 'Dismiss' }, on: { click: (e) => {
    e.stopPropagation();
    dismiss();
  } } }, icon('x'));
  const root = el('div', { class: 'rotate-prompt', attrs: { role: 'status' } },
    el('span', { class: 'rp-phone' }, icon('rotate')),
    el('span', { class: 'rp-text' }, el('b', { text: 'Rotate for the best experience' }), el('small', { text: 'Portrait works too — tap to dismiss' })),
    close);
  root.hidden = true;
  window.addEventListener('pointerdown', onTap, true);
  return {
    el: root,
    update(screen) {
      if (dismissed) return;
      const show = deps.services.input.device === 'touch' && window.innerHeight > window.innerWidth && PLAY_SCREENS.has(screen);
      setHidden(root, !show);
      if (!show) return;
      const now = performance.now();
      if (shownAt < 0) shownAt = now;
      else if (now - shownAt > AUTO_HIDE_MS) dismiss();
    },
  };
}
